import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import * as dayjs from 'dayjs';
import { Logger } from '@app/core/logging';

import { GetPaidService, Country } from '@app/+payment/services/get-paid.service';
import { formatFieldToControlerName } from './fieldToControlerName';
import { stateFieldRequiredCountries } from '@app/+payment/models/extra-field-countries';

@Component({
  selector: 'vc-personal-information',
  templateUrl: './personal-information.component.html',
  styleUrls: ['./personal-information.component.scss']
})
export class PersonalInformationComponent implements OnInit {
  @Input()
  userIsB2C = false; // data to retrieve from user
  @Input()
  isWebView: boolean;
  @Output()
  customerCreated: EventEmitter<boolean> = new EventEmitter<boolean>();

  // form validators: HyperWallet rules, for more details check : https://docs.hyperwallet.com/content/api/v3/resources/users/create
  personalInformation = this.fb.group({
    firstname: ['', [Validators.required, Validators.pattern(/^[a-zA-ZÀ-ÿ\s\'\,\-\.]*$/), Validators.maxLength(50)]],
    lastname: ['', [Validators.required, Validators.pattern(/^[a-zA-ZÀ-ÿ\s\'\,\-\.]*$/), Validators.maxLength(50)]],
    dateOfBirth: this.fb.group({
      day: ['', [Validators.required]],
      month: ['', [Validators.required]],
      year: ['', [Validators.required]]
    })
  });

  c2cContact = this.fb.group({
    phone: ['', [Validators.required, Validators.maxLength(17), Validators.pattern(/^[\d\s.\(\)\.\-+]*$/)]]
  });

  businessName = this.fb.group({
    businessName: [
      '',
      [Validators.required, Validators.maxLength(100), Validators.pattern(/^[a-zA-ZÀ-ÿ\d\s\!\&\'\(\)\+\,\-\.\/\:\;]*$/)]
    ]
  });

  addressForm = this.fb.group({
    street: [
      '',
      [Validators.required, Validators.maxLength(100), Validators.pattern(/^[a-zA-ZÀ-ÿ\d\s\#\'\(\)\,\-\.\/\:\;\°]*$/)]
    ],
    city: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-ZÀ-ÿ\s\&\'\(\)\-\.]*$/)]],
    zip: ['', [Validators.required, Validators.maxLength(16), Validators.pattern(/^[a-zA-ZÀ-ÿ\d\s\-]*$/)]],
    country: ['', [Validators.required]],
    state: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-zA-ZÀ-ÿ\s\&\'\(\)\-]*$/)]]
  });

  form = this.fb.group({
    address: this.addressForm
  });

  isLoading = false;
  error: boolean | string = false;

  // Date
  limitDate = dayjs().subtract(18, 'year');
  limitDateError = false;
  minYear = 1900;
  years: number[] = this.fillYearsOptions();
  locale = '';
  countryList: Country[] = [];
  stateList = {};
  currentStates = [];

  constructor(
    private fb: FormBuilder,
    private getPaidService: GetPaidService,
    private el: ElementRef,
    private logger: Logger,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.locale = this.getPaidService.getUserLang();

    this.getUserAddress();

    if (this.userIsB2C) {
      this.setB2cControls();
    } else {
      this.handleDateOfBirth();
      this.setC2cControls();
    }

    this.handleAddress();
  }

  onSubmit() {
    if (this.form.valid && !this.limitDateError) {
      this.isLoading = true;
      this.error = false;

      this.getPaidService.createCustomer(this.form.value).subscribe(
        res => {
          this.customerCreated.emit(true);
          this.isLoading = false;
        },
        err => {
          this.isLoading = false;

          if (err && err.detail) {
            // handle field error
            if (err.status === 400) {
              this.handleErrorField(err.detail);
              this.scrollToError();
            } else {
              // show error message from api
              this.error = err.detail.message || true;
            }
          } else {
            this.error = true;
          }
        }
      );
    } else {
      this.validateForm(this.form);
      this.scrollToError();
    }
  }

  validateForm(form: FormGroup) {
    Object.keys(form.controls).forEach(field => {
      const control = form.get(field);
      if (control instanceof FormControl) {
        control.markAsDirty({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateForm(control);
      }
    });
  }

  getUserAddress() {
    this.isLoading = true;
    this.getPaidService.getAddressFormData().subscribe(
      ({ address, countryList, stateList }) => {
        this.isLoading = false;

        // set address form fields
        this.countryList = countryList;
        this.stateList = stateList;

        // set user fields
        if (address) {
          const { firstname, lastname, phone, street1, city, zipcode, country, state } = address;
          this.personalInformation.get('firstname').setValue(firstname || '');
          this.personalInformation.get('lastname').setValue(lastname || '');
          this.c2cContact.get('phone').setValue(phone || '');
          this.form.get('address.street').setValue(street1 || '');
          this.form.get('address.city').setValue(city || '');
          this.form.get('address.zip').setValue(zipcode || '');
          this.form.get('address.country').setValue(country.code || '');

          if (this.form.get('address.state') && state && state.iso) {
            this.form.get('address.state').setValue(state.iso || '');
          }
        }
      },
      err => {
        this.isLoading = false;
        this.error = true;
        this.logger.error(`[GET PAID] Personal Information: Cannot get address information`, err);
      }
    );
  }

  setC2cControls() {
    this.getPaidService.getUserDateOfBirth().subscribe(
      date => {
        if (date) {
          const { day, month, year } = date;
          this.personalInformation.get('dateOfBirth.day').setValue(day);
          this.personalInformation.get('dateOfBirth.month').setValue(month);
          this.personalInformation.get('dateOfBirth.year').setValue(year);
        }
      },
      _ => {}
    );

    this.form.removeControl('business');
    this.form.addControl('personalInformation', this.personalInformation);
    this.form.addControl('contact', this.c2cContact);
  }

  setB2cControls() {
    this.form.removeControl('personalInformation');
    this.form.removeControl('contact');
    this.form.addControl('business', this.businessName);
  }

  handleDateOfBirth() {
    this.personalInformation.get('dateOfBirth').valueChanges.subscribe(
      event => {
        const { day, month, year } = event;

        if (day && month && year) {
          this.limitDateError =
            !this.dateIsValid(+day, +month, +year) || dayjs(`${year}-${month}-${day}`).isAfter(this.limitDate);
        }
      },
      _ => {}
    );
  }

  handleAddress() {
    this.form.get('address.country').valueChanges.subscribe(
      event => {
        const country = this.countryList.find(c => c.isoCode.toLowerCase() === event.toLowerCase());
        // reset states infos
        this.currentStates = [];
        if (this.addressForm.get('state')) {
          this.addressForm.get('state').setValue('');
        }

        if (stateFieldRequiredCountries.includes(country.isoCode.toUpperCase())) {
          // IF country requires state field add it
          if (!this.addressForm.get('state')) {
            this.addressForm.addControl(
              'state',
              new FormControl('', [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^[a-zA-ZÀ-ÿ\s\&\'\(\)\-]*$/)
              ])
            );
          }
          this.currentStates = this.stateList[country.isoCode.toLowerCase()] || [];
        } else {
          // remove state field
          if (this.addressForm.get('state')) {
            this.addressForm.removeControl('state');
          }
        }
      },
      _ => {}
    );
  }

  checkField(fieldName: string) {
    return this.form.get(fieldName) && this.form.get(fieldName).invalid && this.form.get(fieldName).dirty;
  }

  fillYearsOptions() {
    const years = [];
    for (let year = dayjs().year(); year >= this.minYear; year--) {
      years.push(year);
    }

    return years;
  }

  dateIsValid(day: number, month: number, year: number): boolean {
    // check min
    if (day === 0 || month === 0 || year < this.minYear) {
      return false;
    }
    // check max
    if (day > 31 || month > 12 || year > dayjs().year()) {
      return false;
    }

    // check february
    if (month === 2) {
      // check year bissextile
      if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
        return day <= 29;
      } else {
        return day <= 28;
      }
    }

    // less than 31 days months
    if ([4, 6, 9, 11].includes(month)) {
      return day < 31;
    }

    return true;
  }

  scrollToError() {
    const invalidElement: HTMLElement = this.el.nativeElement.querySelector('.error .vc-inputText');

    if (invalidElement) {
      invalidElement.focus();
    }
  }

  handleErrorField(errObj) {
    Object.keys(errObj).forEach(key => {
      const value = errObj[key];
      const controlName = formatFieldToControlerName[key];

      if (Array.isArray(value) && controlName) {
        this.form.get(controlName).setErrors({ custom: value.join() });
        this.form.get(controlName).markAsDirty({ onlySelf: true });
      } else if (typeof value === 'object') {
        this.handleErrorField(value);
      }
    });

    // To mark the form changes;
    this.cdr.detectChanges();
  }

  get errorType() {
    return typeof this.error;
  }
}
